import _ from 'lodash'
import { Observable, of, forkJoin, zip } from 'rxjs'
import { catchError, first, map, switchMap, tap } from 'rxjs/operators'

import { ConditionReport, MediaMetaDataResource, MediaUploadResult2, RecordMaskResource } from '@skinsoft/types'
import { EditScreenVCSaveResult, EditScreenViewControllerInterface } from '@skinsoft/classes-interfaces-definition'
import { conditionReportSelectors } from '@skinsoft/selectors'
import { medias, referenceNumber } from '@skinsoft/selectors/condition-report-selectors'
import { ConditionReportDataStore } from '@skinsoft/entity'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { MaskFieldUpdateEvent, MaskFieldUpdateEventType } from '@skinsoft/record-mask/src/components/mask-field'

declare const config
const FIND_MEDIAS_MAX_TRY = config.editScreen.mediaUpload.maxAttempts
const FIND_MEDIAS_REPEAT_DELAY = config.editScreen.mediaUpload.retryDelay

export interface ConditionReportEditScreenControllerCallbacks {
    onEntityFetched: (entity: ConditionReport) => void
    on409Error: () => void
}

export class ConditionReportEditScreenController implements EditScreenViewControllerInterface<ConditionReport> {
    private conditionReportDataStore: ConditionReportDataStore
    private imageTransferHelper: ImageTransferHelper
    private mediaTransferHelper: MediaTransferHelper

    constructor(private callbacks: ConditionReportEditScreenControllerCallbacks) {
        this.conditionReportDataStore = ConditionReportDataStore.getInstance()
        this.imageTransferHelper = ImageTransferHelper.getInstance()
        this.mediaTransferHelper = MediaTransferHelper.getInstance()
    }

    getEntity(id: string): Observable<ConditionReport> {
        return this.conditionReportDataStore.fetchOne(id).pipe(
            tap(entity => {
                if (!entity.associatedMedias) {
                    _.set(entity, 'associatedMedias', {
                        medias: [],
                        first: undefined,
                        second: undefined,
                        third: undefined,
                    })
                }
                this.callbacks.onEntityFetched(entity)
            })
        )
    }

    adaptValuesBeforeSave(entity: ConditionReport, _recordMasks: RecordMaskResource[]) {
        if (entity.conditionReportCS?.signers) {
            for (const signer of entity.conditionReportCS.signers) {
                delete (signer as any).id
            }
        }

        if (entity.conditionReportCS?.counterSignatures) {
            for (const counterSignature of entity.conditionReportCS.counterSignatures) {
                if (counterSignature.observationCarriedPresenceOfs) {
                    for (const counterSigner of counterSignature.observationCarriedPresenceOfs) {
                        delete (counterSigner as any).index
                    }
                }
            }
        }
    }

    saveEntity(entity: ConditionReport, recordMasks: RecordMaskResource[]): Observable<EditScreenVCSaveResult> {
        const entityCopy = _.cloneDeep(entity)
        _.set(entityCopy, 'conditionReportCS.counterSignatures', entity.conditionReportCS?.counterSignatures)

        this.adaptValuesBeforeSave(entityCopy, recordMasks)

        return this.conditionReportDataStore.fetchOne(entity.id).pipe(
            first(),
            tap(currentReport => {
                const deletedMedias: string[] = _.difference(medias(currentReport), medias(entity))
                const localDeletedMedias = deletedMedias.filter(mediaId => mediaId.startsWith('local:'))
                for (const mediaId of localDeletedMedias) {
                    this.imageTransferHelper.deleteImageInCache(mediaId)
                }

                if (!window.navigator.onLine) {
                    const currentReferenceNumber = referenceNumber(currentReport)
                    _.set(entityCopy, 'conditionReportCS.referenceNumber', currentReferenceNumber)
                    _.set(entity, 'conditionReportCS.referenceNumber', currentReferenceNumber)
                }
            }),
            switchMap(() => {
                const uploadImages$ = window.navigator.onLine
                    ? this.uploadImages(entity)
                    : of([] as MediaUploadResult2[])
                return uploadImages$
            }),
            map(mediaUploadResults => {
                const saveResult: EditScreenVCSaveResult = {
                    success: true,
                    mediaUploadResult: mediaUploadResults,
                }
                for (const uploadResult of mediaUploadResults) {
                    if (uploadResult.cacheKey && uploadResult.mediaId) {
                        this.updateMediaId(entity, uploadResult.cacheKey, uploadResult.mediaId)
                        this.updateMediaId(entityCopy, uploadResult.cacheKey, uploadResult.mediaId)
                    } else {
                        saveResult.success = false
                    }
                }
                return saveResult
            }),
            switchMap(saveResult => {
                if (!saveResult.success) {
                    return of(saveResult)
                }
                return this.conditionReportDataStore.update(entity.id, entityCopy).pipe(
                    tap(updatedReport => {
                        Object.assign(entity, updatedReport)
                    }),
                    map(() => saveResult),
                    catchError(error => {
                        if (error?.status === 409) {
                            this.callbacks.on409Error()
                        }
                        saveResult.success = false
                        return of(saveResult)
                    })
                )
            }),
        )
    }

    uploadImage(entity: ConditionReport, mediaCacheKey: string): Observable<MediaUploadResult2> {
        const mediaId = this.mediaTransferHelper.getMediaIdFromCacheKey(mediaCacheKey)

        if (mediaId) {
            this.updateMediaId(entity, mediaCacheKey, mediaId)
            return of({ cacheKey: mediaCacheKey, mediaId } as MediaUploadResult2)
        }

        return this.imageTransferHelper.uploadLocalImageToApi(mediaCacheKey).pipe(
            catchError(() => of({ cacheKey: mediaCacheKey, errorOccured: true, timeOut: false } as MediaUploadResult2)),
            switchMap(uploadResult => {
                if (uploadResult.fileName && !uploadResult.errorOccured) {
                    return zip(
                        this.mediaTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(
                            uploadResult.fileName, FIND_MEDIAS_MAX_TRY, FIND_MEDIAS_REPEAT_DELAY,
                        ),
                        of(uploadResult),
                    )
                }
                uploadResult.errorOccured = true
                return zip(of(null), of(uploadResult))
            }),
            map(([media, uploadResult]) => {
                if (!uploadResult.errorOccured) {
                    if (media?.id) {
                        uploadResult.mediaId = media.id
                    } else {
                        uploadResult.timeOut = true
                        uploadResult.errorOccured = true
                    }
                }
                return uploadResult
            }),
            tap(uploadResult => {
                if (uploadResult.mediaId && uploadResult.cacheKey) {
                    this.mediaTransferHelper.saveMappingCacheKeyMediaId(uploadResult.cacheKey, uploadResult.mediaId)
                    this.updateMediaId(entity, uploadResult.cacheKey, uploadResult.mediaId)
                }
            })
        )
    }

    updateEntity(event: MaskFieldUpdateEvent, entity: Partial<ConditionReport>) {
        if (event.type === MaskFieldUpdateEventType.DELETE) {
            const lastPropInPathRegex = /(\[\d\]|\.[a-zA-Z\d]*)$/
            const path = event.fieldPath
            const pathToLastParent = path.replace(lastPropInPathRegex, '')
            const lastParent = _.get(entity, pathToLastParent)

            if (_.isArray(lastParent)) {
                const indexMatches = path.match(lastPropInPathRegex)
                if (indexMatches?.length) {
                    // @ts-ignore need to upgrade ts (replaceAll)
                    const indexString = indexMatches[0].replaceAll(/\[|\]|\./g, '')
                    const index = parseInt(indexString)
                    if (!isNaN(index)) {
                        lastParent.splice(index, 1)
                    }
                }
            } else {
                _.unset(entity, path)
            }
        } else {
            _.set(entity, event.fieldPath, event.value)
        }
    }

    addImage(mediaId: string, entity: Partial<ConditionReport>) {
        const mediasList = entity?.associatedMedias?.medias ?? []
        if (!entity?.associatedMedias?.medias) {
            _.set(entity, 'associatedMedias.medias', mediasList)
        }
        if (!mediasList.includes(mediaId)) {
            if (entity.associatedMedias?.medias) {
                entity.associatedMedias.medias.push(mediaId)
            } else {
                _.set(entity, 'associatedMedias.medias', [mediaId])
            }
        }
    }

    deleteImage(mediaId: string, entity: ConditionReport) {
        if (mediaId && entity?.associatedMedias?.medias?.length) {
            entity.associatedMedias.medias = entity.associatedMedias.medias.filter(id => id !== mediaId)
        }
    }

    setFirstMedia(mediaId: string, entity: ConditionReport) {
        if (mediaId) {
            this.addImage(mediaId, entity)
            _.set(entity, 'associatedMedias.first', mediaId)
        }
    }

    private uploadImages(entity: ConditionReport): Observable<MediaUploadResult2[]> {
        const cachedKeys = this.getCachedMediasKeys(entity)
        if (!cachedKeys.length) return of([])

        const uploads$: Observable<MediaUploadResult2>[] = []
        for (const cacheKey of cachedKeys) {
            const mediaId = this.mediaTransferHelper.getMediaIdFromCacheKey(cacheKey)
            if (mediaId) {
                this.updateMediaId(entity, cacheKey, mediaId)
            } else {
                uploads$.push(
                    this.imageTransferHelper.uploadLocalImageToApi(cacheKey).pipe(
                        catchError(() => of({ cacheKey, errorOccured: true, timeOut: false } as MediaUploadResult2))
                    )
                )
            }
        }

        if (!uploads$.length) return of([])

        return forkJoin(uploads$).pipe(
            switchMap(allResults => {
                const [withNames, noNames] = _.partition(allResults, r => r.fileName)

                for (const r of allResults) {
                    if (noNames.find(n => n.cacheKey === r.cacheKey)) {
                        r.errorOccured = true
                        r.timeOut = false
                    }
                }

                const fileNames = withNames.map(r => r.fileName!)
                if (fileNames.length) {
                    return zip(
                        this.mediaTransferHelper.repeatFindingMediasIdsFromFileNamesWithLimit(
                            fileNames, FIND_MEDIAS_MAX_TRY, FIND_MEDIAS_REPEAT_DELAY,
                        ),
                        of(allResults),
                    )
                }
                return zip(of([] as MediaMetaDataResource[]), of(allResults))
            }),
            switchMap(([mediasMetaData, results]) => {
                for (const r of results) {
                    const meta = mediasMetaData.find(m => m.metaData?.fileRecordCS?.fileName === r.fileName)
                    if (meta) {
                        r.mediaId = meta.id
                        if (r.cacheKey) {
                            this.mediaTransferHelper.saveMappingCacheKeyMediaId(r.cacheKey, meta.id)
                        }
                    } else if (!r.errorOccured) {
                        r.errorOccured = true
                        r.timeOut = true
                    }
                }
                return of(results)
            })
        )
    }

    private getCachedMediasKeys(entity: ConditionReport): string[] {
        const res: string[] = []
        if (entity?.associatedMedias) {
            if (entity.associatedMedias.first && MediaTransferHelper.isLocalMedia(entity.associatedMedias.first)) {
                res.push(entity.associatedMedias.first)
            }
            if (entity.associatedMedias.second && MediaTransferHelper.isLocalMedia(entity.associatedMedias.second)) {
                res.push(entity.associatedMedias.second)
            }
            if (entity.associatedMedias.third && MediaTransferHelper.isLocalMedia(entity.associatedMedias.third)) {
                res.push(entity.associatedMedias.third)
            }
            if (entity.associatedMedias.medias?.length) {
                for (const mediaId of entity.associatedMedias.medias) {
                    if (MediaTransferHelper.isLocalMedia(mediaId)) {
                        res.push(mediaId)
                    }
                }
            }
        }
        return _.uniq(res)
    }

    private updateMediaId(entity: ConditionReport, oldId: string, newId: string) {
        if (entity?.associatedMedias) {
            if (entity.associatedMedias.first === oldId) entity.associatedMedias.first = newId
            if (entity.associatedMedias.second === oldId) entity.associatedMedias.second = newId
            if (entity.associatedMedias.third === oldId) entity.associatedMedias.third = newId
            if (entity.associatedMedias.medias?.length) {
                entity.associatedMedias.medias = entity.associatedMedias.medias.map(id =>
                    id === oldId ? newId : id
                )
            }
        }
    }
}
